import React, { useState } from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  pdf,
  Font,
} from '@react-pdf/renderer';
import { Tooltip, IconButton } from '@chakra-ui/react';
import { FiFileText } from 'react-icons/fi';
import LogoColegio from '../../assets/img/logoColegio.png';
import FirmaRector from '../../assets/img/firmaRector.png'; // Importa la firma del rector
import { useDispatch, useSelector } from 'react-redux';
import { getAllNotasByStudent } from '../../features/notaSlice';
import globalInformation from '../../helpers/globalInformation';
import { CustomToast } from '../../helpers/toast';
import Helvetica from '../../assets/fonts/Helvetica.ttf';
import HelveticaBold from '../../assets/fonts/Helvetica-Bold.ttf';

// Registrar fuente normal
Font.register({
  family: 'Helvetica',
  src: Helvetica,
});

// Registrar fuente bold como familia separada
Font.register({
  family: 'Helvetica-Bold',
  src: HelveticaBold,
});

// Estilos mejorados para un diseño más profesional
const styles = StyleSheet.create({
  page: {
    padding: 15,
    backgroundColor: '#FFFFFF',
    width: '215.9mm',
    height: '279.4mm',
    fontSize: 10,
    fontFamily: 'Helvetica',
  },

  // Encabezado de la institución - Diseño renovado
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#2C3E50',
  },
  logoContainer: {
    width: 40,
    height: 40,
    marginRight: 15,
  },
  schoolInfoContainer: {
    flex: 1,
  },
  schoolName: {
    fontSize: 12,
    fontWeight: 700,
    color: '#2C3E50',
    marginBottom: 3,
    fontFamily: 'Helvetica-Bold',
  },
  schoolDecrees: {
    fontSize: 7,
    color: '#34495E',
    marginBottom: 1,
  },
  schoolCode: {
    fontSize: 8,
    fontWeight: 600,
    color: '#34495E',
    fontFamily: 'Helvetica-Bold',
    marginTop: 2,
  },

  // Información del estudiante - Más organizada
  studentContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 5,
    backgroundColor: '#F8F9FA',
    borderRadius: 5,
    borderLeftWidth: 4,
    borderLeftColor: '#2fa33a',
  },
  studentInfoColumn: {
    width: '33%',
  },
  studentInfoLabel: {
    fontSize: 7,
    fontWeight: 700,
    color: '#000000',
    marginBottom: 2,
    fontFamily: 'Helvetica-Bold',
  },
  studentInfoValue: {
    fontSize: 6,
    fontWeight: 600,
    color: '#34495E',
    marginBottom: 2,
    fontFamily: 'Helvetica-Bold',
  },

  // Tabla de calificaciones - Estilo moderno
  tableContainer: {
    marginTop: 5,
    marginBottom: 5,
  },
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#BDC3C7',
    borderRadius: 3,
    overflow: 'hidden',
    fontFamily: 'Helvetica',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: '#2fa33a',
    backgroundColor: '#ECF0F1',
    fontFamily: 'Helvetica',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#EAECEE',
    fontFamily: 'Helvetica',
  },
  tableHeaderCell: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    fontSize: 7,
    fontWeight: 700,
    textAlign: 'center',
    color: '#000000',
    borderRightWidth: 1,
    borderRightColor: '#BDC3C7',
    fontFamily: 'Helvetica-Bold',
  },
  tableCell: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    fontSize: 6,
    textAlign: 'center',
    color: '#2C3E50',
    borderRightWidth: 1,
    borderRightColor: '#EAECEE',
    justifyContent: 'center',
  },
  tableCellLeft: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    fontSize: 7,
    textAlign: 'left',
    justifyContent: 'center',
    color: '#2C3E50',
    fontFamily: 'Helvetica',
    borderRightWidth: 1,
    borderRightColor: '#EAECEE',
  },

  // Celdas de notas
  gradeCell: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    fontSize: 7,
    fontWeight: 600,
    textAlign: 'center',
    justifyContent: 'center',
    borderRightWidth: 1,
    borderRightColor: '#EAECEE',
    fontFamily: 'Helvetica',
  },

  // Observaciones en tabla
  observationRow: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderBottomWidth: 1,
    borderBottomColor: '#EAECEE',
    fontFamily: 'Helvetica',
  },
  observationText: {
    fontSize: 6,
    lineHeight: 1.4,
    paddingHorizontal: 6,
    fontWeight: 600,
    color: '#2C3E50',
    fontFamily: 'Helvetica',
  },
  observationLabel: {
    display: 'block',
    fontSize: 6,
    fontWeight: 600,
    color: '#2C3E50',
    lineHeight: 1.2,
  },

  // Tamaños de columnas
  cellSubject: {
    width: '25%',
  },
  cellPeriod: {
    width: '7%',
  },
  cellFinal: {
    width: '7%',
  },
  cellObservation: {
    width: '28%',
  },
  cellResult: {
    width: '12%',
  },
  cellDocente: {
    width: '25%',
  },

  // Sección de resumen y promedios
  summaryContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 5,
    marginBottom: 5,
    fontFamily: 'Helvetica',
  },
  summaryBox: {
    width: '48%',
    padding: 5,
    backgroundColor: '#F8F9FA',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#E5E7E9',
  },
  summaryTitle: {
    fontWeight: 700,
    fontSize: 8,
    color: '#2C3E50',
    marginBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#2fa33a',
    paddingBottom: 3,
    fontFamily: 'Helvetica-Bold',
  },
  summaryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
    fontFamily: 'Helvetica',
  },
  summaryLabel: {
    fontSize: 7,
    color: '#34495E',
    fontFamily: 'Helvetica-Bold',
  },
  summaryValue: {
    fontSize: 7,
    fontWeight: 600,
    color: '#2C3E50',
    fontFamily: 'Helvetica',
  },
  averageValue: {
    fontSize: 8,
    fontWeight: 700,
    textAlign: 'center',
    marginTop: 5,
    paddingHorizontal: 4,
    paddingVertical: 1,
    backgroundColor: '#2fa33a',
    color: 'white',
    borderRadius: 3,
    fontFamily: 'Helvetica',
  },

  // Leyenda de valoración
  legendContainer: {
    marginTop: 5,
    padding: 5,
    backgroundColor: '#F8F9FA',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#E5E7E9',
  },
  legendTitle: {
    fontSize: 8,
    fontWeight: 700,
    color: '#2C3E50',
    marginBottom: 5,
    fontFamily: 'Helvetica',
  },
  legendText: {
    fontSize: 7,
    lineHeight: 1.3,
    color: '#34495E',
    fontFamily: 'Helvetica-Bold',
  },
  legendScale: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 5,
  },
  legendItem: {
    width: '23%',
    padding: 3,
    borderRadius: 3,
    alignItems: 'center',
  },
  legendItemText: {
    fontSize: 6,
    textAlign: 'center',
    color: 'white',
    fontWeight: 600,
    fontFamily: 'Helvetica',
  },

  // Firmas
  signatureSection: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 10,
  },
  signature: {
    width: 200,
    textAlign: 'center',
  },
  signatureLine: {
    borderTopWidth: 1,
    borderTopColor: '#2C3E50',
    marginBottom: 5,
  },
  signatureText: {
    fontSize: 8,
    color: '#34495E',
    textAlign: 'center',
    fontFamily: 'Helvetica',
  },
  signatureImage: {
    width: 100,
    height: 40,
    marginBottom: 2,
    alignSelf: 'center',
  },

  // Badge para desempeño
  performanceBadge: {
    fontSize: 6,
    fontWeight: 700,
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderRadius: 2,
    color: 'white',
    fontFamily: 'Helvetica',
  },
});

// Función para determinar el desempeño según la calificación
const getPerformanceLevel = average => {
  if (average >= 4.7) return 'SUPERIOR';
  if (average >= 4.0) return 'ALTO';
  if (average >= 3.0) return 'BASICO';
  return 'BAJO';
};

// Función para obtener el color según la calificación
const getGradeColor = grade => {
  if (grade >= 4.7) return '#27AE60'; // Verde oscuro - Superior
  if (grade >= 4.0) return '#52C41A'; // Verde claro - Alto
  if (grade >= 3.0) return '#FAAD14'; // Amarillo - Básico
  return '#F5222D'; // Rojo - Bajo (para notas menores a 3.0)
};

// Función para obtener color de fondo suave
const getGradeBackgroundColor = grade => {
  if (grade >= 4.7) return '#F6FFED'; // Verde muy claro - Superior
  if (grade >= 4.0) return '#F6FFED'; // Verde muy claro - Alto
  if (grade >= 3.0) return '#FFFBE6'; // Amarillo muy claro - Básico
  return '#FFF1F0'; // Rojo muy claro - Bajo (para notas menores a 3.0)
};

const API_URL = process.env.REACT_APP_API_URL;
const BASE_URL = API_URL && API_URL.split('/api')[0];

// Componente principal del reporte
const AcademicReportPDF = ({
  studentData,
  subjectData,
  sede,
  totalEstudiantesPorGrado,
  condicionFinal,
  ranking: propsRanking,
  promedioGeneral: propsPromedioGeneral,
  schoolInfo
}) => {
  const student = studentData?.estudiante || {};
  const academicYear = studentData?.academic_year || '2025';
  const grade = studentData?.grado || {};

  // PROMEDIO GENERAL
  // Usar el prop pasado si existe, sino intentar leer de studentData, si no, 0
  const finalPromedioGeneral = propsPromedioGeneral !== undefined ? propsPromedioGeneral : (studentData?.promedioGeneral || 0);

  // Determinar posición ranking
  const position = propsRanking !== undefined ? propsRanking : (studentData?.ranking || '0');

  const logoToUse = schoolInfo?.logoUrl || LogoColegio;
  const schoolNameToUse = schoolInfo?.name || globalInformation?.colegioNombre || 'INSTITUCION EDUCATIVA EL ASERRÍO';

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Encabezado con información de la institución */}
        <View style={styles.headerContainer}>
          <View style={styles.logoContainer}>
            <Image src={logoToUse} />
          </View>
          <View style={styles.schoolInfoContainer}>
            <Text style={styles.schoolName}>
              {schoolNameToUse}
            </Text>
            {globalInformation?.decretos?.map((decreto, index) => (
              <Text key={index} style={styles.schoolDecrees}>
                {decreto?.tipo} N° {decreto.numero} del {decreto.fecha}
              </Text>
            ))}
            {/* SEDE */}
            <Text style={styles.schoolCode}>SEDE: {sede?.nombre}</Text>
            <Text style={styles.schoolCode}>
              CÓDIGO DANE: {sede?.codigoDane}
            </Text>
          </View>
        </View>

        {/* Información del estudiante */}
        <View style={styles.studentContainer}>
          <View style={styles.studentInfoColumn}>
            <Text style={styles.studentInfoLabel}>NOMBRES Y APELLIDOS:</Text>
            <Text style={styles.studentInfoValue}>
              {student?.nombres} {student?.apellidos}
            </Text>
            <Text style={styles.studentInfoLabel}>IDENTIFICACIÓN:</Text>
            <Text style={styles.studentInfoValue}>{student?.dni}</Text>
          </View>
          <View style={styles.studentInfoColumn}>
            <Text style={styles.studentInfoLabel}>GRADO:</Text>
            <Text style={styles.studentInfoValue}>
              {grade?.nombre || '10A'} | {grade?.nivel || 'NIVEL'}
            </Text>
            <Text style={styles.studentInfoLabel}>AÑO LECTIVO:</Text>
            <Text style={styles.studentInfoValue}>{academicYear?.year}</Text>
          </View>
          <View style={styles.studentInfoColumn}>
            <Text style={styles.studentInfoLabel}>PERIODO:</Text>
            <Text style={styles.studentInfoValue}>{academicYear?.periodo}</Text>
            <Text style={styles.studentInfoLabel}>PUESTO:</Text>
            <Text style={styles.studentInfoValue}>
              {position} de {totalEstudiantesPorGrado}
            </Text>
          </View>
        </View>

        {/* Tabla de calificaciones */}
        <View style={styles.tableContainer}>
          <View style={styles.table}>
            {/* Encabezado de la tabla */}
            <View style={styles.tableHeaderRow}>
              <View style={[styles.tableHeaderCell, styles.cellSubject]}>
                <Text>MATERIAS</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellPeriod]}>
                <Text>P. 1</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellPeriod]}>
                <Text>P. 2</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellPeriod]}>
                <Text>P. 3</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellPeriod]}>
                <Text>P. 4</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellFinal]}>
                <Text>PROM.</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellFinal]}>
                <Text>REC.</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellFinal]}>
                <Text>NC</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellFinal]}>
                <Text>IH</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellFinal]}>
                <Text>FAL.</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellResult]}>
                <Text>VAL.</Text>
              </View>
              <View style={[styles.tableHeaderCell, styles.cellDocente]}>
                <Text>DOCENTE</Text>
              </View>
            </View>

            {/* Filas de asignaturas ACADÉMICAS solamente */}
            {(() => {
              const materiasNormales = (subjectData || []).filter(
                s => !s.materia?.esComportamental
              );

              return materiasNormales.map((subject, index) => {
                const esComportamental = false;

                // Para materias comportamentales, obtener las valoraciones
                const valoracion1 = subject?.valoracion1 || '';
                const valoracion2 = subject?.valoracion2 || '';
                const valoracion3 = subject?.valoracion3 || '';
                const valoracion4 = subject?.valoracion4 || '';

                // Para materias normales, calcular notas
                const bimestre1 = parseFloat(subject?.bimestre1) || 0;
                const bimestre2 = parseFloat(subject?.bimestre2) || 0;
                const bimestre3 = parseFloat(subject?.bimestre3) || 0;
                const bimestre4 = parseFloat(subject?.bimestre4) || 0;
                const promedio = parseFloat(subject?.promedio) || 0;
                const recuperacion = parseFloat(subject?.recuperacion) || 0;
                const ncMinimo = 12.0;
                const nc = esComportamental
                  ? 0
                  : ncMinimo - (bimestre1 + bimestre2 + bimestre3 + bimestre4);
                const ih = subject?.materia?.intensidadHorariaSemanal || 0;
                const fallas = subject?.fallas || 0;
                // Determinar periodo actual
                const currentPeriod = parseInt(academicYear?.periodo) || 1;
                // Seleccionar valoraciones y nota del periodo actual
                const valoraciones = [
                  valoracion1,
                  valoracion2,
                  valoracion3,
                  valoracion4,
                ];
                const valoracionPeriodoActual =
                  valoraciones[currentPeriod - 1] || '';
                // Determinar si hay nota de recuperación
                const tieneRecuperacion = recuperacion > 0;

                // Calcular la nota final (si hay recuperación, usar esa, si no, usar el promedio)
                const notaFinal = tieneRecuperacion ? recuperacion : promedio;

                // Valoración y color de acuerdo a la nota final (incluyendo recuperación si existe)
                const performanceLevel = esComportamental
                  ? valoracionPeriodoActual
                  : getPerformanceLevel(notaFinal);

                // Usar el color basado en la nota final (que incluye recuperación si existe)
                const gradeColor = esComportamental
                  ? '#52C41A'
                  : getGradeColor(notaFinal);
                const indicadoresPeriodo =
                  subject?.indicadores?.find(
                    i => i.periodo === parseInt(academicYear?.periodo)
                  )?.indicador || [];

                return (
                  <React.Fragment key={index}>
                    {/* Fila de materia académica */}
                    <View style={[styles.tableRow]}>
                      <View style={[styles.tableCellLeft, styles.cellSubject]}>
                        <Text style={{ fontWeight: 600, fontSize: 7 }}>
                          {subject?.materia?.nombre}
                        </Text>
                      </View>
                      {/* Período 1 */}
                      <View
                        style={[
                          styles.gradeCell,
                          styles.cellPeriod,
                          {
                            backgroundColor:
                              bimestre1 > 0
                                ? getGradeBackgroundColor(bimestre1)
                                : 'transparent',
                          },
                        ]}
                      >
                        <Text
                          style={{
                            fontSize: 7,
                            fontWeight: 600,
                            color:
                              bimestre1 > 0
                                ? getGradeColor(bimestre1)
                                : '#2C3E50',
                          }}
                        >
                          {bimestre1 > 0 ? bimestre1.toFixed(1) : '—'}
                        </Text>
                      </View>

                      {/* Período 2 */}
                      <View
                        style={[
                          styles.gradeCell,
                          styles.cellPeriod,
                          {
                            backgroundColor:
                              bimestre2 > 0
                                ? getGradeBackgroundColor(bimestre2)
                                : 'transparent',
                          },
                        ]}
                      >
                        <Text
                          style={{
                            fontSize: 7,
                            fontWeight: 600,
                            color:
                              bimestre2 > 0
                                ? getGradeColor(bimestre2)
                                : '#2C3E50',
                          }}
                        >
                          {bimestre2 > 0 ? bimestre2.toFixed(1) : '—'}
                        </Text>
                      </View>

                      {/* Período 3 */}
                      <View
                        style={[
                          styles.gradeCell,
                          styles.cellPeriod,
                          {
                            backgroundColor:
                              bimestre3 > 0
                                ? getGradeBackgroundColor(bimestre3)
                                : 'transparent',
                          },
                        ]}
                      >
                        <Text
                          style={{
                            fontSize: 7,
                            fontWeight: 600,
                            color:
                              bimestre3 > 0
                                ? getGradeColor(bimestre3)
                                : '#2C3E50',
                          }}
                        >
                          {bimestre3 > 0 ? bimestre3.toFixed(1) : '—'}
                        </Text>
                      </View>

                      {/* Período 4 */}
                      <View
                        style={[
                          styles.gradeCell,
                          styles.cellPeriod,
                          {
                            backgroundColor:
                              bimestre4 > 0
                                ? getGradeBackgroundColor(bimestre4)
                                : 'transparent',
                          },
                        ]}
                      >
                        <Text
                          style={{
                            fontSize: 7,
                            fontWeight: 600,
                            color:
                              bimestre4 > 0
                                ? getGradeColor(bimestre4)
                                : '#2C3E50',
                          }}
                        >
                          {bimestre4 > 0 ? bimestre4.toFixed(1) : '—'}
                        </Text>
                      </View>

                      {/* Promedio */}
                      <View
                        style={[
                          styles.gradeCell,
                          styles.cellFinal,
                          {
                            backgroundColor:
                              promedio > 0
                                ? getGradeBackgroundColor(promedio)
                                : 'transparent',
                          },
                        ]}
                      >
                        <Text
                          style={{
                            fontSize: 8,
                            fontWeight: 700,
                            color:
                              promedio > 0
                                ? getGradeColor(promedio)
                                : '#2C3E50',
                          }}
                        >
                          {promedio > 0 ? promedio.toFixed(2) : '—'}
                        </Text>
                      </View>

                      {/* Recuperación */}
                      <View
                        style={[
                          styles.gradeCell,
                          styles.cellFinal,
                          {
                            backgroundColor:
                              recuperacion > 0
                                ? getGradeBackgroundColor(recuperacion)
                                : 'transparent',
                          },
                        ]}
                      >
                        <Text
                          style={{
                            fontSize: 7,
                            fontWeight: 600,
                            color:
                              recuperacion > 0
                                ? getGradeColor(recuperacion)
                                : '#2C3E50',
                          }}
                        >
                          {recuperacion > 0 ? recuperacion.toFixed(2) : '—'}
                        </Text>
                      </View>
                      <View style={[styles.tableCell, styles.cellFinal]}>
                        <Text style={{ fontSize: 7 }}>
                          {recuperacion > 0
                            ? '—'
                            : nc > 0
                              ? nc.toFixed(2)
                              : '—'}
                        </Text>
                      </View>
                      <View style={[styles.tableCell, styles.cellFinal]}>
                        <Text style={{ fontSize: 7 }}>{ih > 0 ? ih : '—'}</Text>
                      </View>
                      <View style={[styles.tableCell, styles.cellFinal]}>
                        <Text style={{ fontSize: 7 }}>
                          {fallas > 0 ? fallas : '—'}
                        </Text>
                      </View>
                      <View style={[styles.tableCell, styles.cellResult]}>
                        <View
                          style={[
                            styles.performanceBadge,
                            { backgroundColor: gradeColor },
                          ]}
                        >
                          <Text>{performanceLevel}</Text>
                        </View>
                      </View>
                      <View style={[styles.tableCellLeft, styles.cellDocente]}>
                        <Text style={{ fontSize: 7 }}>
                          {subject?.materia?.docente?.nombre || '—'}
                        </Text>
                      </View>
                    </View>

                    {/* Si hay observaciones, mostrarlas */}
                    {indicadoresPeriodo.length > 0 && (
                      <View style={styles.observationRow}>
                        {indicadoresPeriodo.map((item, index) => (
                          <Text key={index} style={styles.observationLabel}>
                            • {item?.texto || item?.indicador || '—'}
                          </Text>
                        ))}
                      </View>
                    )}
                  </React.Fragment>
                );
              });
            })()}

            {(() => {
              const materiasComportamentales = (subjectData || []).filter(
                s => s.materia?.esComportamental
              );
              const currentPeriod = parseInt(academicYear?.periodo) || 1;

              return materiasComportamentales.map((subject, index) => {
                const valoracion1 = subject?.valoracion1 || '';
                const valoracion2 = subject?.valoracion2 || '';
                const valoracion3 = subject?.valoracion3 || '';
                const valoracion4 = subject?.valoracion4 || '';
                const valoraciones = [
                  valoracion1,
                  valoracion2,
                  valoracion3,
                  valoracion4,
                ];
                const valoracionPeriodoActual =
                  valoraciones[currentPeriod - 1] || '';

                // Solo mostrar si tiene descripción
                if (!valoracionPeriodoActual.trim()) return null;

                return (
                  <View key={`comportamiento-${index}`} style={styles.tableRow}>
                    <View
                      style={[
                        styles.tableCellLeft,
                        {
                          width: '20%',
                          paddingHorizontal: 6,
                          paddingVertical: 2,
                          justifyContent: 'center',
                        },
                      ]}
                    >
                      <Text
                        style={{
                          fontWeight: 700,
                          fontSize: 7,
                          color: '#2C3E50',
                        }}
                      >
                        {subject?.materia?.nombre}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.tableCellLeft,
                        {
                          width: '60%',
                          paddingHorizontal: 6,
                          paddingVertical: 2,
                          justifyContent: 'center',
                        },
                      ]}
                    >
                      <Text
                        style={{
                          fontSize: 6,
                          color: '#34495E',
                          fontWeight: 600,
                          lineHeight: 1.3,
                        }}
                      >
                        {valoracionPeriodoActual}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.tableCellLeft,
                        {
                          width: '20%',
                          paddingHorizontal: 6,
                          paddingVertical: 2,
                          justifyContent: 'center',
                        },
                      ]}
                    >
                      <Text style={{ fontSize: 7, color: '#34495E' }}>
                        {grade?.docente_titular?.nombre ||
                          subject?.materia?.docente?.nombre ||
                          '—'}
                      </Text>
                    </View>
                  </View>
                );
              });
            })()}
          </View>
        </View>

        {/* Sección de resumen */}
        <View style={styles.summaryContainer}>
          <View style={styles.summaryBox}>
            <Text style={styles.summaryTitle}>RESUMEN ACADÉMICO</Text>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Materias evaluadas:</Text>
              <Text style={styles.summaryValue}>
                {(subjectData || []).filter(s => !s.materia?.esComportamental)
                  .length || 0}
              </Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Puesto del grado:</Text>
              <Text style={styles.summaryValue}>
                {position} de {totalEstudiantesPorGrado}
              </Text>
            </View>
            <Text style={styles.averageValue}>
              PROMEDIO GENERAL: {finalPromedioGeneral}
            </Text>
          </View>
          <View style={styles.summaryBox}>
            <Text style={styles.summaryTitle}>ESCALA DE VALORACIÓN</Text>
            <View style={styles.legendScale}>
              <View style={[styles.legendItem, { backgroundColor: '#F5222D' }]}>
                <Text style={styles.legendItemText}>BAJO</Text>
                <Text style={styles.legendItemText}>1.0 - 2.9</Text>
              </View>
              <View style={[styles.legendItem, { backgroundColor: '#FAAD14' }]}>
                <Text style={styles.legendItemText}>BÁSICO</Text>
                <Text style={styles.legendItemText}>3.0 - 3.9</Text>
              </View>
              <View style={[styles.legendItem, { backgroundColor: '#52C41A' }]}>
                <Text style={styles.legendItemText}>ALTO</Text>
                <Text style={styles.legendItemText}>4.0 - 4.6</Text>
              </View>
              <View style={[styles.legendItem, { backgroundColor: '#27AE60' }]}>
                <Text style={styles.legendItemText}>SUPERIOR</Text>
                <Text style={styles.legendItemText}>4.7 - 5.0</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Sección del estado final del estudiante */}

        {/* solo que se muestre cuando esté en el 4 periodo */}
        {parseInt(academicYear?.periodo) === 4 && (
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: 2,
              marginBottom: 2,
              fontWeight: '900',
            }}
          >
            <Text
              style={{
                fontSize: 8,
                fontWeight: 900,
                fontFamily: 'Helvetica-Bold',
                marginRight: 2,
              }}
            >
              SITUACIÓN PROMOCIONAL:
            </Text>
            <Text
              style={{
                fontSize: 8,
                fontWeight: 500,
                fontFamily: 'Helvetica',
              }}
            >
              {condicionFinal}
            </Text>
          </View>
        )}

        {/* Sección de firmas */}
        <View style={styles.signatureSection}>
          <View style={styles.signature}>
            <Image src={FirmaRector} style={styles.signatureImage} />
            <View style={styles.signatureLine}></View>
            <Text style={styles.signatureText}>Rector(a)</Text>
            <Text style={[styles.signatureText, { fontWeight: 700 }]}>
              {globalInformation?.rector || 'NOMBRE DEL RECTOR(A)'}
            </Text>
          </View>
          <View style={styles.signature}>
            <View style={styles.signatureImage} />
            <View style={styles.signatureLine}></View>
            <Text style={styles.signatureText}>Docente Titular</Text>
            <Text style={[styles.signatureText, { fontWeight: 700 }]}>
              {grade?.docente_titular?.nombre || 'NOMBRE DEL DOCENTE TITULAR'}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// Componente botón para generar y descargar el reporte
const ReportButton = ({ data, configuracion, getTotalEstudiantesPorGrado }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const dispatch = useDispatch();

  const { sedeSeleccionada, user } = useSelector(state => state.auth);

  const defaultSchoolInfo = {
    name: configuracion?.nombreColegio || globalInformation.colegioNombre || 'INSTITUCION EDUCATIVA EL ASERRÍO',
    logoUrl: configuracion?.logo ? `${BASE_URL}/uploads/${configuracion.logo}` : globalInformation.logoColegio,
    address: globalInformation.direccionColegio,
    phone: globalInformation.telefonoColegio,
    email: globalInformation.emailColegio,
  };

  const generateAndDownloadPdf = async () => {
    const totalEstudiantesPorGrado = await getTotalEstudiantesPorGrado();

    if (!data?._id) {
      CustomToast({
        title: 'Error',
        message: 'No se encontraron datos del estudiante',
        type: 'error',
        duration: 3000,
        position: 'top',
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Obtener notas del estudiante
      const notas = await dispatch(getAllNotasByStudent(data)).unwrap();

      // Validar que haya datos
      if (!notas?.notas || !notas?.notas?.length) {
        throw new Error(
          'No se encontraron datos de calificaciones para este estudiante'
        );
      }

      // Preparar información de la institución
      const schoolInfo = {
        name: configuracion?.nombreColegio || globalInformation?.colegioNombre || 'INSTITUCION EDUCATIVA EL ASERRÍO',
        logoUrl: configuracion?.logo ? `${BASE_URL}/uploads/${configuracion.logo}` : LogoColegio
      };

      // Generar el PDF
      const pdfDoc = (
        <AcademicReportPDF
          studentData={data}
          sede={sedeSeleccionada}
          subjectData={notas?.notas || []}
          schoolInfo={schoolInfo}
          totalEstudiantesPorGrado={notas?.totalEstudiantes || totalEstudiantesPorGrado}
          condicionFinal={notas?.condicionFinal}
          ranking={notas?.ranking}
          promedioGeneral={notas?.promedioGeneral}
        />
      );

      // Crear blob y descargar
      const blob = await pdf(pdfDoc).toBlob();
      const url = URL.createObjectURL(blob);

      const fileName = `boletin_${data?.estudiante?.nombres
        ?.toLowerCase()
        .replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      CustomToast({
        title: 'Éxito',
        message: 'Boletín académico generado y descargado correctamente',
        type: 'success',
        duration: 3000,
        position: 'top',
      });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      CustomToast({
        title: 'Error',
        message:
          error.msg ||
          'Error al generar el boletín académico revise si tiene notas',
        type: 'error',
        duration: 3000,
        position: 'top',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Tooltip label="Generar boletín académico" placement="auto">
      <IconButton
        icon={<FiFileText />}
        size="md"
        mr={2}
        color={'white'}
        colorScheme="primary"
        _dark={{
          bg: 'primary.500',
          color: 'white',
          _hover: { bg: 'primary.600' },
        }}
        isRound
        variant="solid"
        aria-label="Generar boletín PDF"
        isLoading={isGenerating}
        onClick={generateAndDownloadPdf}
        isDisabled={
          user?.usuario?.rol !== 'ADMIN_ROLE' &&
          configuracion?.permitirDescargarBoletin === false
        }
      />
    </Tooltip>
  );
};

export default ReportButton;

